/* AMSEC Product Configurator — data-driven engine, identical to product-configurator-liberty.js.
   Reads option_group/option_value/model/model_size_configuration metaobject data
   serialized by product-configurator-amsec.liquid. Step order, compatibility, and
   pricing all come from that data — nothing here is specific to any one brand or series
   (the one Liberty-specific check, the 'paint_style' handle, is simply never matched by
   AMSEC's option groups and is a harmless no-op here). */
(function () {
  'use strict';

  function money(n) {
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  function delta(n) {
    return n ? '+ ' + money(n) : 'Included';
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
  // Size-card tooltips are '·'-joined specs (e.g. "Ext 59.25\" x 24\" x 21\" · Int 53\" x 20\" x 14\"
  // · 751 lbs") meant to read as one spec per line — plain wrapped text let the layout break
  // mid-spec instead (e.g. "751" stranded on one line and "lbs" alone on the next). Splitting on
  // the delimiter and joining with <br> forces each spec onto its own line regardless of width.
  function formatSpecLines(s) {
    return String(s == null ? '' : s).split(' · ').map(esc).join('<br>');
  }
  // Resolves once the image is loaded (or fails) so a hero/sticky-cart layer swap never
  // reveals a half-loaded stack. Resolving on error too keeps a bad URL from hanging the swap.
  function preloadImage(url) {
    if (!url) return Promise.resolve();
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = img.onerror = () => resolve();
      img.src = url;
    });
  }

  function init(root) {
    // Key Features grid (server-rendered <i data-lucide="..."> icons) needs the same
    // library + call as safe-builder.js to actually render as SVGs. Called unconditionally,
    // ahead of the data.model early-return below, since the icons are static markup that
    // exists regardless of whether the configurator itself has data.
    if (typeof lucide !== 'undefined') lucide.createIcons();

    const sectionId = root.dataset.sectionId;
    const dataEl = document.getElementById('lc-data-' + sectionId);
    if (!dataEl) return;

    let data;
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.error('AMSEC configurator: failed to parse data', e);
      return;
    }

    const $ = (id) => document.getElementById(id + '-' + sectionId);
    const stepsEl = $('lc-steps');
    const priceTopEl = $('lc-price-top');
    const priceTotalEl = $('lc-price-total');
    const priceNoteEl = $('lc-price-note');
    const configStringEl = $('lc-config-string');
    const derivedEl = $('lc-derived');
    const includedEl = $('lc-included');
    const ctaEl = $('lc-cta');
    const ctaHelperEl = $('lc-cta-helper');
    const heroEl = $('lc-hero');
    const heroHardwareEl = $('lc-hero-hardware');
    const heroFinialEl = $('lc-hero-finial');
    const heroLogoEl = $('lc-hero-logo');
    const frameEl = heroEl ? heroEl.closest('.lc__frame') : null;
    const stickyCartEl = $('lc-sticky-cart');
    const stickyCartToggleEl = $('lc-sticky-cart-toggle');
    const stickyCartImageEl = $('lc-sticky-cart-image');
    const stickyCartHardwareEl = $('lc-sticky-cart-image-hardware');
    const stickyCartFinialEl = $('lc-sticky-cart-image-finial');
    const stickyCartLogoEl = $('lc-sticky-cart-image-logo');
    const thumbConfiguredBtn = root.querySelector('[data-lc-thumb-configured]');
    const thumbConfiguredBody = $('lc-thumb-configured-body');
    const thumbConfiguredHardware = $('lc-thumb-configured-hardware');
    const thumbConfiguredFinial = $('lc-thumb-configured-finial');
    const thumbConfiguredLogo = $('lc-thumb-configured-logo');
    const stickyCartVariantEl = $('lc-sticky-cart-variant');
    const stickyCartPriceEl = $('lc-sticky-cart-price');
    const stickyCartJumpEl = $('lc-sticky-cart-jump');
    const stickyCartAddEl = $('lc-sticky-cart-add');
    const stickyCartAddLabelEl = $('lc-sticky-cart-add-label');
    const specCapacityEl = $('lc-spec-capacity');
    const specWeightEl = $('lc-spec-weight');
    // Bumped on every hero image swap so a slower-loading, now-superseded swap can tell
    // (once its preloads resolve) that a newer selection already won and skip applying.
    let heroSwapToken = 0;

    if (!data.model) {
      if (ctaEl) {
        ctaEl.disabled = true;
        ctaEl.textContent = 'Configurator unavailable';
      }
      if (stickyCartAddEl) stickyCartAddEl.disabled = true;
      if (stickyCartAddLabelEl) stickyCartAddLabelEl.textContent = 'Configurator unavailable';
      return;
    }

    const sizeConfigs = (data.modelSizeConfigurations || []).filter(Boolean);
    const configuredImages = (data.configuredImages || []).filter(Boolean);
    // Layered configured-safe imagery: a "body" layer (per size + color) and a
    // "composite" layer carrying logo/handle/lock pre-combined (per size + hardware
    // finish + lock), each resolved and swapped independently, then stacked via CSS.
    // See safe_image_layer metaobjects / amsec-safe-images/ for how these are authored.
    const bodyImageLayers = configuredImages.filter((entry) => entry.layerRole === 'body');
    const hardwareImageLayers = configuredImages.filter((entry) => entry.layerRole === 'composite');
    // Finial handle overlay: an independent decorative hardware layer (BFX 6030+ only) that
    // doesn't touch or replace the lock/handle-style composite — it's photographed and
    // positioned separately (opposite the hinge), so it's its own layer/img/option-group
    // rather than a required_option variant of the composite layer. Gated on/off entirely by
    // the 'amsec_finials' selection, same as the logo layer below, not matched against lock
    // type or handle style. Uses the schema's 'handle' layer_role (defined but unused until
    // now — safe_image_layer.layer_role is a constrained field; 'finial' isn't one of its
    // allowed values, 'handle' is the one meant for a standalone handle graphic).
    const finialImageLayers = configuredImages.filter((entry) => entry.layerRole === 'handle');
    // Logo overlay layer, independent of the hardware composite (BF1512: the AMSEC logo can be
    // removed via the 'amsec_logo' option without needing separate logo-less hardware photos).
    const logoImageLayers = configuredImages.filter((entry) => entry.layerRole === 'logo');
    const groupsById = new Map(data.optionGroups.map((g) => [g.id, g]));
    const availableGroupIds = new Set(data.model.availableOptionGroupIds || []);

    const sizeGroup = data.optionGroups.find((g) => g.role === 'size' && availableGroupIds.has(g.id));
    const otherGroups = data.optionGroups
      .filter((g) => g.role !== 'size' && availableGroupIds.has(g.id))
      .sort((a, b) => a.sortOrder - b.sortOrder);

    const valuesByGroup = new Map();
    for (const v of data.optionValues) {
      if (!v.groupId) continue;
      if (!valuesByGroup.has(v.groupId)) valuesByGroup.set(v.groupId, []);
      valuesByGroup.get(v.groupId).push(v);
    }
    // No-cost options first, then cheapest to priciest; sortOrder only breaks ties within
    // the same price (e.g. multiple $0 or multiple +$150 finishes keep their curated order).
    for (const list of valuesByGroup.values()) {
      list.sort((a, b) => (a.priceModifierValue || 0) - (b.priceModifierValue || 0) || a.sortOrder - b.sortOrder);
    }

    function findValueByCode(groupId, code) {
      return (valuesByGroup.get(groupId) || []).find((v) => v.code === code) || null;
    }

    // A value's real upcharge can vary by size when it carries dependencies.price_by_size =
    // { [sizeCode]: amount } (optionally { [modelId]: { [sizeCode]: amount } } model-scoped first)
    // — the same fallback chain pricingEngine.ts already implements server-side and
    // product-configurator.js already implements for Fort Knox, kept identical here per
    // pricingEngine.ts's own header comment: flat priceModifierValue wins whenever it's genuinely
    // set (nonzero); dependencies.price_by_size is only consulted when the flat value is 0/unset.
    // Needed because AMSEC body-color option_values are shared across every model (BF/BFX/BFII/RF
    // all reference the same 'onyx'/'txt-black'/etc. metaobjects), but the manufacturer's real
    // Gloss/Two-Tone/Wrap upcharge scales by model+size, not a single flat delta — see AUG 2025
    // RETAIL WITH MAP CSV. No-op for any value without a matching entry, so options that really
    // are flat-priced everywhere (locks, hinges, packaging, etc.) are unaffected.
    // Returns { amount, isDynamic } rather than a bare number: the client asked that a
    // size-varying upcharge (resolved from dependencies.price_by_size, e.g. AMSEC's Gloss
    // colors) never show its "+$X" badge in the picker at all — the amount is inherently
    // tied to whichever size happens to be selected, unlike a flat price_modifier_value
    // (locks, hardware, etc.), which is the same regardless of size and should keep showing
    // its badge. isDynamic only affects display; the resolved amount still feeds the running
    // total either way.
    function effectiveModifier(value) {
      const flat = Number(value.priceModifierValue) || 0;
      if (flat !== 0) return { amount: flat, isDynamic: false };

      const sizeCode = state.sizeConfig ? state.sizeConfig.sizeCode : null;
      const bySize = value.dependencies && value.dependencies.price_by_size;
      if (bySize && sizeCode) {
        const modelId = data.model && data.model.id;
        const modelPrices = modelId ? bySize[modelId] : null;
        if (modelPrices && Object.prototype.hasOwnProperty.call(modelPrices, sizeCode)) {
          return { amount: Number(modelPrices[sizeCode]) || 0, isDynamic: true };
        }
        if (Object.prototype.hasOwnProperty.call(bySize, sizeCode)) {
          return { amount: Number(bySize[sizeCode]) || 0, isDynamic: true };
        }
      }
      return { amount: flat, isDynamic: false };
    }

    // Size cards render straight from sizeConfigs (buildSizeStep) and applyDefaultSelections
    // auto-picks sizeConfigs[0] as the starting build — both need this array in the intended
    // display/priority order, not shop.metaobjects.model_size_configuration.values' raw
    // storage order (which reflects creation order and has no relation to which size a model
    // wants shown/selected first). The size option_value's own sortOrder is per-value (not a
    // shared/ambiguous isDefault flag), so it's a safe per-model ranking signal; falls back to
    // the existing weight/price heuristic when sortOrder doesn't discriminate (e.g. a model
    // that never curated it), so this is a no-op for every model that hasn't set it.
    if (sizeGroup) {
      const sizeMetric = (c) => (c.weightLbs != null ? Number(c.weightLbs) : Number(c.basePrice));
      sizeConfigs.sort((a, b) => {
        const av = findValueByCode(sizeGroup.id, a.sizeCode);
        const bv = findValueByCode(sizeGroup.id, b.sizeCode);
        const aOrder = av && av.sortOrder != null ? av.sortOrder : Number.MAX_SAFE_INTEGER;
        const bOrder = bv && bv.sortOrder != null ? bv.sortOrder : Number.MAX_SAFE_INTEGER;
        return aOrder - bOrder || sizeMetric(a) - sizeMetric(b);
      });
    }

    // A group's default value: a per-model override (data.model.defaultSelections, keyed by
    // group handle — e.g. BF overrides the shared hardware_finish default of Black Nickel to
    // Chrome) takes priority over the group-wide option_value.isDefault flag. Lets shared
    // option_values (reused across every AMSEC/Liberty line) carry one "usual" default while
    // still letting an individual model's real standard configuration differ.
    //
    // allowedCodes (optional) scopes the isDefault fallback search to only codes valid for the
    // current model/size — matters for any shared group that could someday hold more than one
    // isDefault:true value (one per model), same pattern as product-configurator-liberty.js.
    const modelDefaultOverrides = (data.model && data.model.defaultSelections) || {};
    function defaultValueForGroup(group, allowedCodes) {
      const overrideCode = modelDefaultOverrides[group.handle];
      if (overrideCode) {
        const overridden = findValueByCode(group.id, overrideCode);
        if (overridden && (!allowedCodes || allowedCodes.indexOf(overridden.code) !== -1)) return overridden;
      }
      const candidates = valuesByGroup.get(group.id) || [];
      const scoped = allowedCodes ? candidates.filter((v) => allowedCodes.indexOf(v.code) !== -1) : candidates;
      // scoped is already curated no-cost-first/cheapest-first order (see valuesByGroup's own
      // sort above), so falling back to its first entry when nothing is explicitly flagged
      // is exactly "the first option in this group" — every group lands on a real selection
      // instead of leaving the shopper to click through every step manually.
      return scoped.find((v) => v.isDefault) || scoped[0] || null;
    }

    const state = {
      sizeConfig: null,
      selections: {}, // groupHandle -> code
      clearedNotice: null, // groupHandle of a selection just cleared by a dependency change
    };

    function allowedCodesForGroup(group) {
      if (!state.sizeConfig) return null; // null = "choose a size first"
      const fromSize = state.sizeConfig.availableOptionCodes || [];
      return fromSize;
    }

    /** Dependency-based filtering (e.g. accent color -> allowed body colors), by group role. */
    function dependencyAllowList(group) {
      for (const otherGroup of otherGroups) {
        const selectedCode = state.selections[otherGroup.handle];
        if (!selectedCode) continue;
        const selectedValue = findValueByCode(otherGroup.id, selectedCode);
        if (!selectedValue || !selectedValue.dependencies) continue;
        const allowed = selectedValue.dependencies.allowed_values;
        if (allowed && Object.prototype.hasOwnProperty.call(allowed, group.handle)) {
          return allowed[group.handle];
        }
      }
      return null;
    }

    // Conditional steps (e.g. Lincoln's Paint Style, which exists only on Premium Signature
    // trim — not rendered at all otherwise, no disabled placeholder). A group with no
    // visibleWhen is always visible (every existing group, on every model, today).
    function isGroupVisible(group) {
      if (group.visibleWhen) {
        const selected = state.selections[group.visibleWhen.group];
        if (!(selected && group.visibleWhen.codes.indexOf(selected) !== -1)) return false;
      }
      // A group whose every value has been excluded from this size's available_options
      // (e.g. BFX6024 can't take a Finials handle — the Finials group's values are all left
      // out of its model_size_configuration) doesn't exist on this size at all. Hide the
      // whole step instead of showing an empty "Select an option" picker with nothing to
      // pick — the size-scoped exclusion is meant to remove the option, not just the choices.
      const sizeAllowed = allowedCodesForGroup(group);
      if (sizeAllowed) {
        const values = valuesByGroup.get(group.id) || [];
        if (values.length && !values.some((v) => sizeAllowed.indexOf(v.code) !== -1)) return false;
      }
      return true;
    }

    // What a group's selection "counts as" for dependencies.requires matching elsewhere (e.g.
    // body_color values keyed to a paint_style code). A visible group's real selection is used
    // as-is. A group that isn't even shown right now (e.g. Paint Style on Standard/Pro Flex,
    // where there is no 2-Tone concept at all) counts as its default value instead of nothing —
    // otherwise every requires-gated value in a dependent group would wrongly exclude itself
    // forever, since an unselected hidden step can never literally match a required code.
    function effectiveSelection(handle) {
      const groupDef = otherGroups.find((g) => g.handle === handle);
      if (groupDef && !isGroupVisible(groupDef)) {
        // A group like "paint_style" can be shared across models (Lincoln and Presidential
        // both use it) — its option_values include both brands' codes under one group id.
        // Scope the default lookup to codes this model's current size actually offers,
        // otherwise this could pick another model's default (e.g. LINCOLN_1TONE) instead of
        // this one's (PRESIDENTIAL_1TONE), and every requires-gated value would still fail
        // to match.
        const sizeAllowed = (state.sizeConfig && state.sizeConfig.availableOptionCodes) || [];
        const defaultValue = defaultValueForGroup(groupDef, sizeAllowed);
        if (defaultValue) return defaultValue.code;
      }
      return state.selections[handle];
    }

    function buildSizeStep() {
      const step = document.createElement('section');
      step.className = 'lc__step';
      const groupLabel = sizeGroup ? sizeGroup.titleName || sizeGroup.displayName : 'Choose your size';
      step.innerHTML =
        '<div class="lc__step-head"><h2 class="lc__step-title">' + esc(groupLabel) + '</h2>' +
        '<span class="lc__step-value" data-lc-size-label>Select a size</span></div>' +
        '<div class="lc__grid" style="--lc-cols:3" data-lc-size-options role="group" aria-label="' + esc(groupLabel) + '"></div>';
      stepsEl.appendChild(step);

      const optionsEl = step.querySelector('[data-lc-size-options]');
      optionsEl.innerHTML = sizeConfigs
        .map((config) => {
          const value = sizeGroup ? findValueByCode(sizeGroup.id, config.sizeCode) : null;
          const img = value && value.sizeImage ? '<img src="' + esc(value.sizeImage) + '" alt="">' : '';
          // data-lc-size-meta lets render() swap this text for a trim-specific capacity line
          // (see updateSizeCapacities) once both size and trim are selected — e.g. the
          // Colonial 50's capacity differs between Standard and Pro Flex trim.
          const meta = value && value.tooltip
            ? '<span class="lc__opt-meta" data-lc-size-meta="' + esc(config.id) + '">' + formatSpecLines(value.tooltip) + '</span>'
            : '';
          return (
            '<button type="button" class="lc__opt lc__opt--size" data-lc-size="' + esc(config.id) + '" aria-pressed="false">' +
            img +
            '<span class="lc__opt-name">' + esc(config.name) + '</span>' +
            meta +
            '</button>'
          );
        })
        .join('');
    }

    function buildOptionStep(group) {
      const step = document.createElement('section');
      step.className = 'lc__step';
      step.dataset.lcGroupHandle = group.handle;
      const title = group.titleName || group.displayName;
      step.innerHTML =
        '<div class="lc__step-head"><h2 class="lc__step-title">' + esc(title) + '</h2>' +
        '<span class="lc__step-value" data-lc-label>Select an option</span></div>' +
        (group.description ? '<p class="lc__step-description">' + esc(group.description) + '</p>' : '') +
        (group.uiType === 'swatch'
          ? '<div class="lc__swatch-row" data-lc-options role="group" aria-label="' + esc(title) + '"></div>'
          : '<div class="lc__grid" style="--lc-cols:' + (group.columnAmount || 3) + '" data-lc-options role="group" aria-label="' + esc(title) + '"></div>') +
        '<p class="lc__step-note" data-lc-note aria-live="polite"></p>';
      stepsEl.appendChild(step);
    }

    function renderOptionsFor(group) {
      const step = stepsEl.querySelector('[data-lc-group-handle="' + group.handle + '"]');
      if (!step) return;
      const optionsEl = step.querySelector('[data-lc-options]');
      const noteEl = step.querySelector('[data-lc-note]');
      const labelEl = step.querySelector('[data-lc-label]');
      const allValues = valuesByGroup.get(group.id) || [];
      const sizeAllowed = allowedCodesForGroup(group);
      const depAllowed = dependencyAllowList(group);

      if (!sizeAllowed) {
        optionsEl.innerHTML = '';
        noteEl.textContent = 'Choose a size first.';
        labelEl.textContent = 'Select a size first';
        return;
      }

      // Option groups are shared across every Liberty series (Franklin, Colonial, ...), so
      // valuesByGroup can hold another series' values too. Only render the ones that actually
      // belong to this model/size — anything else isn't "temporarily unavailable" (which is
      // what disabled-but-visible communicates), it's just irrelevant to this product.
      let values = allValues.filter((v) => sizeAllowed.indexOf(v.code) !== -1);

      // dependencies.requires excludes a value from the picker entirely unless the referenced
      // selections match — e.g. Lincoln's 4 2-Tone colorways only exist when paint_style is
      // 2-Tone, and its 15 1-Tone colors only exist when paint_style is 1-Tone. Two disjoint
      // value pools in the same group, cleanly swapped rather than shown disabled. No-op for
      // any value without a requires clause (everything on Franklin/Colonial today).
      values = values.filter((v) => {
        const requires = v.dependencies && v.dependencies.requires;
        if (!requires) return true;
        return Object.keys(requires).every((h) => effectiveSelection(h) === requires[h]);
      });

      const isBody = group.role === 'body_color';
      const isLock = group.role === 'lock_type';

      // When a size (e.g. BFII6030+) only ever allows one value in this group (e.g. amsec_handle's
      // 5-spoke — the size has no 3-spoke/L-handle option to switch to), it isn't really a choice
      // — it's mandatory equipment for that size. Force it selected and render it locked (no click
      // affordance, no upcharge badge) instead of a normal priced, deselectable-looking button,
      // which otherwise implied the customer could decline it and skip the price.
      const onlyValue = values.length === 1 && (!depAllowed || depAllowed.indexOf(values[0].code) !== -1)
        ? values[0]
        : null;
      if (onlyValue && state.selections[group.handle] !== onlyValue.code) {
        state.selections[group.handle] = onlyValue.code;
      }
      const selectedCode = state.selections[group.handle];

      optionsEl.innerHTML = values
        .map((v) => {
          const locked = Boolean(onlyValue) && v.code === onlyValue.code;
          const disabled = Boolean(depAllowed && depAllowed.indexOf(v.code) === -1);
          const pressed = selectedCode === v.code;
          const priceMod = effectiveModifier(v);
          const priceForDisplay = priceMod.isDynamic ? 0 : priceMod.amount;
          const priceLabel = !locked && priceForDisplay ? '<span class="lc__opt-price">' + delta(priceForDisplay) + '</span>' : '';
          const priceSwatchLabel = !locked && priceForDisplay ? '<span class="lc__swatch-price">' + delta(priceForDisplay) + '</span>' : '';
          const tag = locked ? 'div' : 'button';
          const typeAttr = locked ? '' : ' type="button"';
          const valueAttr = locked ? '' : ' data-lc-value="' + esc(v.code) + '"';

          if (group.uiType === 'swatch') {
            const chipClass = 'lc__swatch-chip' + (isBody ? ' lc__swatch-chip--body' : '');
            // Two-Tone colors (e.g. Lincoln's 4 colorways) render as a split gradient — the
            // body color over a fixed black accent band — matching the source design's
            // twoToneAccent treatment, not a flat chip like a normal 1-Tone/accent swatch.
            const chip = v.finishType === 'two_tone' && v.hexColor
              ? '<span class="' + chipClass + '" style="background:linear-gradient(180deg,' + esc(v.hexColor) + ' 0 68%,#16181A 68% 100%)" aria-hidden="true"></span>'
              : v.optionImage
              ? '<span class="' + chipClass + '" style="background-image:url(' + esc(v.optionImage) + ')" aria-hidden="true"></span>'
              : v.hexColor
              ? '<span class="' + chipClass + '" style="background:' + esc(v.hexColor) + '" aria-hidden="true"></span>'
              : '<span class="' + chipClass + ' lc__swatch-chip--' + esc((v.code || '').toLowerCase()) + '" aria-hidden="true"></span>';
            const swatchMeta = v.tooltip ? '<span class="lc__swatch-meta">' + esc(v.tooltip) + '</span>' : '';
            return (
              '<' + tag + typeAttr + ' class="lc__swatch' + (isBody ? ' lc__swatch--body' : '') + (locked ? ' lc__swatch--locked' : '') + '"' + valueAttr + ' aria-pressed="' + pressed + '" aria-disabled="' + disabled + '" title="' + esc(locked ? v.label + ', the only option for this size' : disabled ? v.label + ', not available with your current selections' : v.label) + '">' +
              chip + '<span class="lc__swatch-name">' + esc(v.label) + '</span>' + swatchMeta + priceSwatchLabel + '</' + tag + '>'
            );
          }

          const img = v.optionImage ? '<img src="' + esc(v.optionImage) + '" alt="">' : '';
          if (isLock) {
            return (
              '<' + tag + typeAttr + ' class="lc__opt lc__opt--lock' + (locked ? ' lc__opt--locked' : '') + '"' + valueAttr + ' aria-pressed="' + pressed + '" aria-disabled="' + disabled + '">' +
              img + '<span class="lc__opt-text"><span class="lc__opt-name">' + esc(v.label) + '</span>' +
              (v.tooltip ? '<span class="lc__opt-meta">' + esc(v.tooltip) + '</span>' : '') +
              priceLabel + '</span></' + tag + '>'
            );
          }
          return (
            '<' + tag + typeAttr + ' class="lc__opt' + (locked ? ' lc__opt--locked' : '') + '"' + valueAttr + ' aria-pressed="' + pressed + '" aria-disabled="' + disabled + '">' +
            img + '<span class="lc__opt-name">' + esc(v.label) + '</span>' +
            (v.tooltip ? '<span class="lc__opt-meta">' + esc(v.tooltip) + '</span>' : '') +
            priceLabel +
            '</' + tag + '>'
          );
        })
        .join('');

      const selectedValue = selectedCode ? findValueByCode(group.id, selectedCode) : null;
      labelEl.textContent = selectedValue ? selectedValue.label : 'Select an option';
      if (state.clearedNotice === group.handle) {
        noteEl.textContent = 'Your previous selection isn’t available with your current choices — pick a new option.';
      } else {
        noteEl.textContent = '';
      }
    }

    // Trim-dependent capacity text on size cards (e.g. the Colonial 50 shows "60 to 64 long
    // guns..." until a trim is picked, then the trim-specific figure). No-op for any size
    // option_value without a dependencies.capacity_by_trim entry (e.g. all of Franklin's).
    function updateSizeCapacities() {
      if (!sizeGroup) return;
      const trimCode = state.selections.trim_level;
      sizeConfigs.forEach((config) => {
        const metaEl = stepsEl.querySelector('[data-lc-size-meta="' + config.id + '"]');
        if (!metaEl) return;
        const value = findValueByCode(sizeGroup.id, config.sizeCode);
        const byTrim = value && value.dependencies && value.dependencies.capacity_by_trim;
        const text = byTrim && trimCode && byTrim[trimCode] ? byTrim[trimCode] : (value ? value.tooltip : '');
        if (text) metaEl.innerHTML = formatSpecLines(text);
      });
    }

    // Key Features strip (Capacity / Weight) — the only two tiles that vary by size, since
    // this configurator has no Shopify variant to hang per-size metafields on. Mirrors
    // safe-builder.liquid's "Select a size to view" fallback so the tiles read the same way
    // before a size is picked, and gracefully show nothing extra when a size has no gun
    // capacity data at all (e.g. BF sizes, which have no published gun-capacity spec).
    function updateKeyFeatures() {
      if (specCapacityEl) {
        if (!state.sizeConfig) {
          specCapacityEl.textContent = 'Select a size to view';
        } else if (state.sizeConfig.gunCapacity) {
          specCapacityEl.textContent = 'Up to ' + state.sizeConfig.gunCapacity + ' guns';
        } else if (state.sizeConfig.cubicFt) {
          // Non-gun-safe capacity (commercial/depository safes, cabinets, vault doors, etc.) —
          // cubic feet instead of "None". Client feedback 2026-09-23.
          specCapacityEl.textContent = state.sizeConfig.cubicFt + ' cu. ft.';
        } else {
          specCapacityEl.textContent = 'None';
        }
      }
      if (specWeightEl) {
        specWeightEl.textContent = state.sizeConfig
          ? (state.sizeConfig.weightLbs ? Number(state.sizeConfig.weightLbs).toLocaleString('en-US') + ' lbs' : '—')
          : 'Select a size to view';
      }
    }

    // Real configured-safe photography (safe_image_layer metaobjects, AI-recolored per size +
    // body color + hardware color + lock combination — see safe-images/ workflow). A photo
    // "matches" the current selection if, for each of its four axis lists that are non-empty,
    // the corresponding current selection appears in that list. An empty list on an entry means
    // "doesn't care about this axis" (e.g. a photo that looks identical across locks). No-op
    // (returns null, falls back to the product's normal media) for any size/color/hardware/lock
    // combination that hasn't been photographed yet.
    const bodyColorGroup = otherGroups.find((g) => g.role === 'body_color') || null;
    const hardwareColorGroup = otherGroups.find((g) => g.role === 'hardware_color') || null;
    const lockTypeGroup = otherGroups.find((g) => g.role === 'lock_type') || null;
    // Not role-tagged in the option_group data (role: 'other' for both) — matched by their
    // known handle instead, same convention this file already uses for the 'paint_style' handle.
    const hingeGroup = otherGroups.find((g) => g.handle === 'amsec_hinge') || null;
    const logoGroup = otherGroups.find((g) => g.handle === 'amsec_logo') || null;
    // Handle Style (3-spoke/5-spoke/l-handle) — hardware composite photography for models
    // with more than one handle style (e.g. BFX) is shot per handle style, so a composite's
    // required_option axis (a generic "this layer only applies when this option_value is
    // selected" field the metaobject schema already had, previously unused) disambiguates
    // which photo to show. Models with only one handle style leave required_option empty on
    // every layer, so this is a no-op for them.
    const handleGroup = otherGroups.find((g) => g.handle === 'amsec_handle') || null;

    /**
     * Finds the most specific safe_image_layer match: every non-empty constraint axis on a
     * layer must contain the current selection for that axis (an empty axis list means "applies
     * regardless"), and among layers that match, the one constraining the most axes wins. This
     * is the same resolver algorithm the metaobject schema was designed for (see
     * CONFIGURATOR_ARCHITECTURE_PLAN.md §3.3) so it works for any future size/color/hardware/lock/
     * hinge-swing combination, not just the BF1512 set authored so far.
     */
    function bestImageMatch(layers, criteria) {
      const axisOk = (list, current) => !list || list.length === 0 || (Boolean(current) && list.indexOf(current) !== -1);
      let best = null;
      let bestSpecificity = -1;
      for (const entry of layers) {
        if (
          !axisOk(entry.sizeCodes, criteria.size) ||
          !axisOk(entry.colorCodes, criteria.color) ||
          !axisOk(entry.hardwareColorCodes, criteria.hardwareColor) ||
          !axisOk(entry.lockTypeCodes, criteria.lockType) ||
          !axisOk(entry.hingeSwingCodes, criteria.hingeSwing) ||
          !axisOk(entry.requiredOptionCodes, criteria.requiredOption)
        ) {
          continue;
        }
        const specificity =
          (entry.sizeCodes && entry.sizeCodes.length ? 1 : 0) +
          (entry.colorCodes && entry.colorCodes.length ? 1 : 0) +
          (entry.hardwareColorCodes && entry.hardwareColorCodes.length ? 1 : 0) +
          (entry.lockTypeCodes && entry.lockTypeCodes.length ? 1 : 0) +
          (entry.hingeSwingCodes && entry.hingeSwingCodes.length ? 1 : 0) +
          (entry.requiredOptionCodes && entry.requiredOptionCodes.length ? 1 : 0);
        if (specificity > bestSpecificity) {
          best = entry;
          bestSpecificity = specificity;
        }
      }
      return best;
    }

    function updateConfiguredHero() {
      if (!heroEl) return;
      // Remember the product's own default hero image once, so we can restore it when the
      // current selection no longer matches any photographed combination.
      if (heroEl.dataset.lcDefaultSrc === undefined) heroEl.dataset.lcDefaultSrc = heroEl.getAttribute('src') || '';

      const sizeCode = state.sizeConfig ? state.sizeConfig.sizeCode : null;
      const colorCode = bodyColorGroup ? state.selections[bodyColorGroup.handle] : null;
      const hardwareCode = hardwareColorGroup ? state.selections[hardwareColorGroup.handle] : null;
      const lockCode = lockTypeGroup ? state.selections[lockTypeGroup.handle] : null;
      // The option_value codes are 'left-hinge'/'right-hinge', but safe_image_layer's
      // hinge_swing field is constrained to the bare choices 'left'/'right' — normalize here
      // rather than in the metaobject data, so the option_value codes can stay descriptive.
      const rawHingeCode = hingeGroup ? state.selections[hingeGroup.handle] : null;
      const hingeCode = rawHingeCode ? rawHingeCode.replace('-hinge', '') : null;
      const logoCode = logoGroup ? state.selections[logoGroup.handle] : null;
      const handleCode = handleGroup ? state.selections[handleGroup.handle] : null;

      const bodyMatch = sizeCode ? bestImageMatch(bodyImageLayers, { size: sizeCode, color: colorCode, hingeSwing: hingeCode }) : null;
      const targetBodyUrl = bodyMatch ? bodyMatch.imageUrl : heroEl.dataset.lcDefaultSrc;

      // Only ever composite the hardware/logo overlays on top of a real, photographed body
      // image (bodyMatch) — never on the product's default/placeholder photo. Without this
      // gate, picking a hardware finish + lock before a body color (or on any combo the body
      // layer has no photography for) would still resolve a match on its own axes and draw
      // hinges/dial/handle/logo floating over an unrelated stock photo.
      const hwMatch = sizeCode && bodyMatch
        ? bestImageMatch(hardwareImageLayers, { size: sizeCode, hardwareColor: hardwareCode, lockType: lockCode, hingeSwing: hingeCode, requiredOption: handleCode })
        : null;
      const targetHwUrl = hwMatch ? hwMatch.imageUrl : null;

      // Finial handle overlay always renders when a matching photo exists — it is not gated
      // by the 'amsec_finials' selection (client feedback 2026-09-28: the handle in this photo
      // isn't the Finials add-on itself, so "No Finials" shouldn't hide it). The option group
      // stays in place for whatever it still drives (e.g. pricing); it just no longer disambiguates
      // this image. Independent hardware (see finialImageLayers above) — only size/hardware
      // finish/hinge side disambiguate the photo.
      const finialMatch = sizeCode && bodyMatch
        ? bestImageMatch(finialImageLayers, { size: sizeCode, hardwareColor: hardwareCode, hingeSwing: hingeCode })
        : null;
      const targetFinialUrl = finialMatch ? finialMatch.imageUrl : null;

      // Only show the logo layer at all when the 'amsec_logo' selection isn't "remove the
      // logo". Any other value (including no selection yet) shows it, since the default
      // configuration keeps the logo.
      const wantsLogo = logoCode !== 'logo-deletion';
      const logoMatch = wantsLogo && sizeCode && bodyMatch
        ? bestImageMatch(logoImageLayers, { size: sizeCode, hardwareColor: hardwareCode, hingeSwing: hingeCode })
        : null;
      const targetLogoUrl = logoMatch ? logoMatch.imageUrl : null;

      // Remembered so add-to-cart can carry the same photo through as hidden line item
      // properties (cart-products.liquid reads these to show the configured safe, not the
      // generic placeholder-variant image). Cleared when nothing matches, same as the hero.
      state.configuredImageUrl = bodyMatch ? bodyMatch.imageUrl : null;
      state.configuredHardwareImageUrl = targetHwUrl;

      const currentHwUrl = heroHardwareEl && heroHardwareEl.classList.contains('is-visible') ? heroHardwareEl.src : null;
      const currentFinialUrl = heroFinialEl && heroFinialEl.classList.contains('is-visible') ? heroFinialEl.src : null;
      const currentLogoUrl = heroLogoEl && heroLogoEl.classList.contains('is-visible') ? heroLogoEl.src : null;
      if (
        heroEl.src === targetBodyUrl &&
        currentHwUrl === targetHwUrl &&
        currentFinialUrl === targetFinialUrl &&
        currentLogoUrl === targetLogoUrl
      ) {
        return; // nothing actually changed — skip the preload/swap dance entirely
      }

      // Preload the full new layer stack (body + hardware + finial + logo) off-screen and swap
      // all four in one go only once every layer that's changing has finished loading, with a
      // skeleton shimmer over the frame in the meantime. Otherwise the small overlay PNGs
      // (which finish loading almost instantly) would render on top of the still-loading,
      // much larger previous-size body photo — a flash of the new hardware/logo mismatched
      // against the old body image. The sticky add-to-cart thumbnail mirrors the same swap
      // so it's never a step behind (or a layer short of) the main hero image.
      const swapToken = ++heroSwapToken;
      if (frameEl) frameEl.classList.add('is-swapping');

      Promise.all([preloadImage(targetBodyUrl), preloadImage(targetHwUrl), preloadImage(targetFinialUrl), preloadImage(targetLogoUrl)]).then(() => {
        if (swapToken !== heroSwapToken) return; // superseded by a newer selection mid-load

        heroEl.src = targetBodyUrl;
        heroEl.alt = bodyMatch ? data.model.name + ', configured' : heroEl.alt;

        if (heroHardwareEl) {
          if (targetHwUrl) {
            heroHardwareEl.src = targetHwUrl;
            heroHardwareEl.classList.add('is-visible');
          } else {
            heroHardwareEl.classList.remove('is-visible');
          }
        }
        if (heroFinialEl) {
          if (targetFinialUrl) {
            heroFinialEl.src = targetFinialUrl;
            heroFinialEl.classList.add('is-visible');
          } else {
            heroFinialEl.classList.remove('is-visible');
          }
        }
        if (heroLogoEl) {
          if (targetLogoUrl) {
            heroLogoEl.src = targetLogoUrl;
            heroLogoEl.classList.add('is-visible');
          } else {
            heroLogoEl.classList.remove('is-visible');
          }
        }

        if (stickyCartImageEl) stickyCartImageEl.src = targetBodyUrl;
        if (stickyCartHardwareEl) {
          if (targetHwUrl) {
            stickyCartHardwareEl.src = targetHwUrl;
            stickyCartHardwareEl.classList.add('is-visible');
          } else {
            stickyCartHardwareEl.classList.remove('is-visible');
          }
        }
        if (stickyCartFinialEl) {
          if (targetFinialUrl) {
            stickyCartFinialEl.src = targetFinialUrl;
            stickyCartFinialEl.classList.add('is-visible');
          } else {
            stickyCartFinialEl.classList.remove('is-visible');
          }
        }
        if (stickyCartLogoEl) {
          if (targetLogoUrl) {
            stickyCartLogoEl.src = targetLogoUrl;
            stickyCartLogoEl.classList.add('is-visible');
          } else {
            stickyCartLogoEl.classList.remove('is-visible');
          }
        }

        // "Your build" thumbnail (first slot in the rail) mirrors the same layer stack, so
        // the thumbnails stay a live preview of the current configuration, not just the
        // main hero. Reusing the exact same URLs the hero/sticky-cart just loaded — no
        // extra network fetch, the browser's HTTP cache already has them.
        if (thumbConfiguredBody) thumbConfiguredBody.src = targetBodyUrl;
        if (thumbConfiguredHardware) {
          if (targetHwUrl) {
            thumbConfiguredHardware.src = targetHwUrl;
            thumbConfiguredHardware.classList.add('is-visible');
          } else {
            thumbConfiguredHardware.classList.remove('is-visible');
          }
        }
        if (thumbConfiguredFinial) {
          if (targetFinialUrl) {
            thumbConfiguredFinial.src = targetFinialUrl;
            thumbConfiguredFinial.classList.add('is-visible');
          } else {
            thumbConfiguredFinial.classList.remove('is-visible');
          }
        }
        if (thumbConfiguredLogo) {
          if (targetLogoUrl) {
            thumbConfiguredLogo.src = targetLogoUrl;
            thumbConfiguredLogo.classList.add('is-visible');
          } else {
            thumbConfiguredLogo.classList.remove('is-visible');
          }
        }
        // The hero now genuinely shows the configuration again (a selection changed, or a
        // static thumbnail's clear-overlay handler ran and this recomputed a real match) —
        // reflect that in the rail's active state even if the shopper never clicked this
        // thumb directly.
        setActiveThumb(thumbConfiguredBtn);

        if (frameEl) frameEl.classList.remove('is-swapping');
      });
    }

    // Single source of truth for "which thumbnail is active" — both the configured-build
    // thumb and every static product-media thumb go through this, so exactly one is ever
    // marked active regardless of which kind of thumb triggered the change.
    function setActiveThumb(activeEl) {
      root.querySelectorAll('.lc__thumb').forEach((el) => {
        const isActive = el === activeEl;
        el.classList.toggle('lc__thumb--active', isActive);
        el.setAttribute('aria-pressed', String(isActive));
      });
    }

    // Derived summary line (e.g. Colonial's "Interior fabric: Camel/Gray Trunkliner", set by
    // the body color once an accent is also chosen) and included-accessory lines (e.g. the
    // Colonial 50's Rifle Rod Kit, included only in Standard trim). Both are data-driven via
    // option_value.dependencies — no-ops when that data isn't present, so Franklin is unaffected.
    function updateDerivedAndIncludedLines() {
      if (derivedEl) {
        // First match wins, in step order — e.g. Lincoln's trim (Premium Signature's fixed
        // Diamond PVC interior) must override accent/body's derived velour color entirely,
        // not just be the last one checked. Step order already puts trim before accent
        // before body, so "first match, then stop" gives the right priority for free.
        let derivedText = '';
        for (const g of otherGroups) {
          if (!isGroupVisible(g)) continue;
          const code = state.selections[g.handle];
          const value = code ? findValueByCode(g.id, code) : null;
          const derived = value && value.dependencies && value.dependencies.derived_label;
          if (derived && derived.key && derived.value) {
            derivedText = derived.key + ': ' + derived.value;
            break;
          }
        }
        derivedEl.textContent = derivedText;
        derivedEl.hidden = !derivedText;
      }

      if (includedEl) {
        const includedCodes = (state.sizeConfig && state.sizeConfig.includedOptionCodes) || [];
        const activeLabels = [];
        for (const code of includedCodes) {
          const value = data.optionValues.find((v) => v.code === code);
          if (!value) continue;
          const requires = value.dependencies && value.dependencies.requires;
          const matches = !requires || Object.keys(requires).every((handle) => effectiveSelection(handle) === requires[handle]);
          if (matches) activeLabels.push(value.label);
        }
        includedEl.textContent = activeLabels.length ? 'Included: ' + activeLabels.join(', ') : '';
        includedEl.hidden = activeLabels.length === 0;
      }
    }

    function render() {
      // Size step
      stepsEl.querySelectorAll('[data-lc-size]').forEach((el) => {
        el.setAttribute('aria-pressed', String(Boolean(state.sizeConfig && String(state.sizeConfig.id) === el.dataset.lcSize)));
      });
      const sizeLabelEl = stepsEl.querySelector('[data-lc-size-label]');
      if (sizeLabelEl) sizeLabelEl.textContent = state.sizeConfig ? state.sizeConfig.name : 'Select a size';
      updateSizeCapacities();
      updateKeyFeatures();

      // Conditional steps (e.g. Paint Style) toggle in/out of the DOM entirely here — a
      // hidden step is skipped for rendering, pricing, and the missing-selections count below,
      // exactly as if it didn't exist on this model at all.
      otherGroups.forEach((g) => {
        const step = stepsEl.querySelector('[data-lc-group-handle="' + g.handle + '"]');
        const visible = isGroupVisible(g);
        if (step) step.hidden = !visible;
        if (visible) renderOptionsFor(g);
      });
      updateDerivedAndIncludedLines();
      updateConfiguredHero();

      // Price + summary
      let total = state.sizeConfig ? Number(state.sizeConfig.basePrice) : 0;
      const parts = [state.sizeConfig ? state.sizeConfig.name : data.model.name];
      const missing = [];
      if (!state.sizeConfig) missing.push('a size');

      otherGroups.forEach((g) => {
        if (!isGroupVisible(g)) return;
        const code = state.selections[g.handle];
        const value = code ? findValueByCode(g.id, code) : null;
        if (value) {
          total += effectiveModifier(value).amount;
          parts.push(value.label);
        } else {
          missing.push((g.titleName || g.displayName).toLowerCase());
          parts.push(g.displayName);
        }
      });

      if (priceTopEl) priceTopEl.textContent = money(total);
      if (priceTotalEl) priceTotalEl.textContent = money(total);
      if (priceNoteEl) {
        priceNoteEl.textContent = missing.length ? 'Estimated with the options selected so far' : 'Configured price, before tax and delivery';
      }
      if (configStringEl) configStringEl.textContent = parts.join(' / ');

      if (ctaEl) {
        ctaEl.disabled = missing.length > 0;
        ctaEl.textContent = missing.length ? 'Select ' + missing[0] + ' to continue' : 'Add to Cart - ' + money(total);
      }

      // Sticky cart bar mirrors the real CTA exactly — same disabled state and
      // price, so there's never a version where the two disagree.
      if (stickyCartPriceEl) stickyCartPriceEl.textContent = money(total);
      if (stickyCartVariantEl) {
        stickyCartVariantEl.textContent = parts.length > 1 ? parts.slice(1).join(' / ') : 'Choose your configuration';
      }
      if (stickyCartAddEl) stickyCartAddEl.disabled = missing.length > 0;
      if (stickyCartAddLabelEl) {
        stickyCartAddLabelEl.textContent = missing.length ? 'Select ' + missing[0] : 'Add to Cart - ' + money(total);
      }

      if (ctaHelperEl) {
        ctaHelperEl.textContent = missing.length ? 'Still needed: ' + missing.join(', ') + '.' : '';
      }
    }

    // Restores a previously-saved configuration when arriving from the cart's "Edit
    // configuration" button (see configurator-cart-line.js), which passes the saved
    // selection/session as query params instead of leaving the shopper to start over blank.
    // No-op (silently falls through to the normal blank-start state) if the params are
    // missing, the selection can't be found, or it doesn't match this model's sizes.
    async function restoreFromQueryParams() {
      const params = new URLSearchParams(window.location.search);

      // Simple size preselect from the collection page's "Choose from N Sizes" card — no
      // draft-order/session round-trip needed, just points state.sizeConfig at the chosen
      // size so the shopper lands mid-flow instead of starting the configurator blank.
      const preselectSize = params.get('size');
      if (preselectSize) {
        const config = sizeConfigs.find((c) => c.sizeCode === preselectSize);
        if (config) state.sizeConfig = config;
        params.delete('size');
        history.replaceState(null, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''));
      }

      const selectionId = params.get('restore_selection');
      const sessionToken = params.get('restore_token');
      if (!selectionId || !sessionToken) return;

      try {
        const response = await fetch(data.apiBase + '/configurator-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ selectionId, sessionToken }),
        });
        if (!response.ok) return;
        const result = await response.json();
        if (!result.found || !result.selections) return;

        const saved = result.selections;
        if (saved.size) {
          const config = sizeConfigs.find((c) => c.sizeCode === saved.size);
          if (config) state.sizeConfig = config;
        }
        otherGroups.forEach((g) => {
          if (saved[g.handle]) state.selections[g.handle] = saved[g.handle];
        });
      } catch (error) {
        console.warn('AMSEC configurator: failed to restore saved selection', error);
      } finally {
        // Clean the URL regardless of outcome so a refresh/share doesn't keep re-triggering
        // this (or, on failure, keep pointing at a now-stale/consumed selection).
        params.delete('restore_selection');
        params.delete('restore_token');
        const query = params.toString();
        history.replaceState(null, '', window.location.pathname + (query ? '?' + query : ''));
      }
    }

    // Pre-selects every group's default value (e.g. BF's "BF1512, Black Textured, Chrome
    // Hardware, ESL10 Lock, Right Hand" standard build), including the size step itself, via
    // the same option_value.isDefault flag every other group already uses — so the shopper
    // lands on a complete, orderable configuration with nothing left to pick.
    // Seeded before restoreFromQueryParams() runs so a real saved selection still wins.
    function applyDefaultSelections() {
      if (!state.sizeConfig && sizeConfigs.length) {
        // sizeConfigs is already sorted (see the sort right after findValueByCode above) by
        // each size's own sortOrder, falling back to the weight/price heuristic when a model
        // hasn't curated sortOrder — so the first entry is the correct starting build either
        // way. Deliberately doesn't use the shared size option_value's isDefault flag: that
        // flag is shared across every model on this line (BF, BFX, BFII, RF, ...), so it can
        // only ever mark one model's preferred size and is wrong for every other one.
        state.sizeConfig = sizeConfigs[0];
      }
      otherGroups.forEach((g) => {
        // Must scope to this size's availableOptionCodes, same as effectiveSelection()
        // and buildOptionStep() do — otherwise a shared group with more than one
        // isDefault:true value (one per model, e.g. amsec_handle's L-Handle for
        // BF/RF vs. 3-Spoke for BFX) picks whichever flagged value sorts first
        // group-wide (cheapest first) instead of the one actually valid for this
        // model's size, even when that value isn't offered on this model at all.
        const defaultValue = defaultValueForGroup(g, allowedCodesForGroup(g));
        if (defaultValue) state.selections[g.handle] = defaultValue.code;
      });
    }

    function buildAll() {
      // A model with only one size has nothing to choose between — applyDefaultSelections()
      // below still auto-selects that one size into state.sizeConfig, so skip rendering a
      // "pick one of one" step instead of showing a pointless single-card grid.
      if (sizeConfigs.length > 1) buildSizeStep();
      otherGroups.forEach((g) => buildOptionStep(g));
      applyDefaultSelections();
      restoreFromQueryParams().finally(render);
    }

    // Sticky cart bar — same show/hide-on-scroll technique as
    // safe-builder.liquid's #sticky-cart (IntersectionObserver on the real
    // CTA), so this reads as the same feature across every PDP on the site.
    function initStickyCart() {
      if (!stickyCartEl || !ctaEl) return;

      // The jump button's visibility depends on TWO independent signals —
      // whether the real CTA is off-screen (the observer below) AND whether
      // the mobile card is collapsed (the toggle below) — funneled through
      // one function so there's a single place setting its inline style.
      // Setting it from the observer alone fights with the CSS collapsed-
      // state display:none rule (inline always wins over a class-based rule
      // regardless of specificity): once the CTA scrolled out of view the
      // button stayed visible even while "collapsed", sitting in a grid area
      // the collapsed 1-row layout doesn't have — scrambling the price/
      // image/button positions.
      let ctaOffScreen = false;
      function updateJumpVisibility() {
        if (!stickyCartJumpEl) return;
        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        const collapsedOnMobile = isMobile && !stickyCartEl.classList.contains('expanded');
        stickyCartJumpEl.style.display = (ctaOffScreen && !collapsedOnMobile) ? 'flex' : 'none';
      }

      if (typeof IntersectionObserver !== 'undefined') {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // isIntersecting alone can't tell "scrolled past it" (top < 0)
              // from "haven't scrolled down to it yet" (still below the
              // viewport) — both report false. On a tall hero the real CTA
              // starts below the fold, so without this check the sticky bar
              // showed immediately at the top of the page instead of only
              // once the shopper actually scrolls past the real button.
              const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              stickyCartEl.classList.toggle('visible', scrolledPast);
              ctaOffScreen = scrolledPast;
              updateJumpVisibility();
            });
          },
          { threshold: 0.1 }
        );
        observer.observe(ctaEl);
      }

      window.addEventListener('resize', updateJumpVisibility, { passive: true });

      if (stickyCartAddEl) {
        stickyCartAddEl.addEventListener('click', () => {
          if (!ctaEl.disabled) ctaEl.click();
        });
      }

      if (stickyCartJumpEl) {
        stickyCartJumpEl.addEventListener('click', () => {
          ctaEl.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        });
      }

      // Mobile collapse/expand handle — desktop's floating pill is small
      // enough already and hides this button entirely via CSS, so this is a
      // no-op there regardless of state.
      if (stickyCartToggleEl) {
        stickyCartToggleEl.addEventListener('click', () => {
          const expanded = stickyCartEl.classList.toggle('expanded');
          stickyCartToggleEl.setAttribute('aria-expanded', String(expanded));
          stickyCartToggleEl.setAttribute('aria-label', expanded ? 'Hide product details' : 'Show product details');
          updateJumpVisibility();
        });
      }
    }

    stepsEl.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn) return;

      if (btn.dataset.lcSize) {
        const config = sizeConfigs.find((c) => String(c.id) === btn.dataset.lcSize);
        if (!config) return;
        state.sizeConfig = config;
        // Clear any selections no longer valid for this size
        otherGroups.forEach((g) => {
          const code = state.selections[g.handle];
          if (code && config.availableOptionCodes.indexOf(code) === -1) {
            delete state.selections[g.handle];
          }
        });
        // Default any group that's newly valid on this size but was never selected — e.g. a
        // group like Finials that the starting/smallest size (BFX6024) doesn't offer at all
        // never gets a default from applyDefaultSelections() at page load (there's nothing
        // valid to default to yet), so switching up to a size that DOES offer it (BFX6030+)
        // left the shopper with no selection at all and forced to pick one before checkout —
        // client feedback 2026-09-23 ("BFX Finials: please auto-select No Finials"). Scoped to
        // this size's own availableOptionCodes, same as the clear step above and
        // defaultValueForGroup's normal usage everywhere else.
        otherGroups.forEach((g) => {
          if (state.selections[g.handle]) return;
          const defaultValue = defaultValueForGroup(g, allowedCodesForGroup(g));
          if (defaultValue) state.selections[g.handle] = defaultValue.code;
        });
        state.clearedNotice = null;
        return render();
      }

      const step = btn.closest('[data-lc-group-handle]');
      if (step && btn.dataset.lcValue) {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        const handle = step.dataset.lcGroupHandle;
        const group = otherGroups.find((g) => g.handle === handle);
        const nextCode = btn.dataset.lcValue;
        state.selections[handle] = nextCode;
        state.clearedNotice = null;

        // If this selection filters another group (e.g. accent -> body), clear any now-invalid
        // downstream selection instead of auto-swapping it.
        const nextValue = findValueByCode(group.id, nextCode);
        if (nextValue && nextValue.dependencies && nextValue.dependencies.allowed_values) {
          Object.keys(nextValue.dependencies.allowed_values).forEach((targetHandle) => {
            const targetCode = state.selections[targetHandle];
            if (targetCode && nextValue.dependencies.allowed_values[targetHandle].indexOf(targetCode) === -1) {
              delete state.selections[targetHandle];
              state.clearedNotice = targetHandle;
            }
          });
        }

        // Switching a paint-style-like axis always clears body_color — its two pools (e.g.
        // Lincoln's 15 1-Tone colors vs 4 2-Tone colorways) are disjoint value sets, so a
        // carried-over selection would otherwise sit there invalid. No-op for any model
        // without a "paint_style" group.
        if (handle === 'paint_style' && state.selections.body_color) {
          delete state.selections.body_color;
        }

        // A conditional step just going out of scope (e.g. trim changing away from Premium
        // Signature hides Paint Style) leaves its old selection stale — reset it to its
        // default value the same way it started, and re-run the allowed_values cascade for
        // that default so anything it used to lock (e.g. 2-Tone forcing a Black accent) is
        // released too. No-op for models with no visibleWhen-gated groups.
        otherGroups.forEach((otherGroup) => {
          if (otherGroup.handle === handle || isGroupVisible(otherGroup)) return;
          const currentCode = state.selections[otherGroup.handle];
          const defaultValue = defaultValueForGroup(otherGroup);
          if (!currentCode || !defaultValue || currentCode === defaultValue.code) return;
          state.selections[otherGroup.handle] = defaultValue.code;
          if (otherGroup.handle === 'paint_style') delete state.selections.body_color;
          if (defaultValue.dependencies && defaultValue.dependencies.allowed_values) {
            Object.keys(defaultValue.dependencies.allowed_values).forEach((targetHandle) => {
              const targetCode = state.selections[targetHandle];
              if (targetCode && defaultValue.dependencies.allowed_values[targetHandle].indexOf(targetCode) === -1) {
                delete state.selections[targetHandle];
              }
            });
          }
        });

        return render();
      }
    });

    if (root.querySelector('[data-lc-photo]')) {
      root.addEventListener('click', (e) => {
        const thumb = e.target.closest('[data-lc-photo]');
        if (!thumb) return;
        if (heroEl) {
          heroEl.src = thumb.dataset.lcPhoto;
          heroEl.alt = thumb.dataset.lcPhotoAlt || '';
        }
        // A raw product-media thumbnail is never one of our photographed body images, so
        // the hardware overlay (drawn to sit on top of a specific body photo) must be
        // cleared too — otherwise it stays composited over whatever generic photo was
        // just clicked in, same bug as showing it with no body color selected.
        if (heroHardwareEl) heroHardwareEl.classList.remove('is-visible');
        if (heroFinialEl) heroFinialEl.classList.remove('is-visible');
        if (heroLogoEl) heroLogoEl.classList.remove('is-visible');
        state.configuredImageUrl = null;
        state.configuredHardwareImageUrl = null;
        setActiveThumb(thumb);
      });
    }

    // "Your build" thumbnail — clicking it hands the hero back to the live configuration
    // (re-running the same match/preload/swap updateConfiguredHero() already does), which
    // matters once a static product-media thumb above has taken over the hero. Its own
    // is-swapping/active-state bookkeeping happens inside updateConfiguredHero() itself, the
    // same as any other selection change, so this click just needs to trigger that.
    if (thumbConfiguredBtn) {
      thumbConfiguredBtn.addEventListener('click', () => {
        setActiveThumb(thumbConfiguredBtn);
        updateConfiguredHero();
      });
    }

    // Main-image prev/next arrows — advance through the same [data-lc-photo]
    // thumbnails the rail already handles, by dispatching a real click at the
    // target thumb so the existing delegated handler above (src swap +
    // clearing the configured hardware overlay) runs unchanged.
    (function initFrameArrows() {
      const prevBtn = root.querySelector('[data-lc-frame-prev]');
      const nextBtn = root.querySelector('[data-lc-frame-next]');
      const allThumbs = root.querySelectorAll('[data-lc-photo]');
      if (!prevBtn || !nextBtn || !allThumbs.length) return;

      function clickRelative(direction) {
        const thumbs = Array.from(allThumbs);
        const activeIndex = thumbs.findIndex((t) => t.classList.contains('lc__thumb--active'));
        const from = activeIndex === -1 ? 0 : activeIndex;
        const nextIndex = (from + direction + thumbs.length) % thumbs.length;
        thumbs[nextIndex].click();
      }

      prevBtn.addEventListener('click', () => clickRelative(-1));
      nextBtn.addEventListener('click', () => clickRelative(1));
    })();

    // Mobile-only nav arrows for the thumbnail rail (a vertical column on
    // desktop, a horizontally-scrollable row on mobile — see the CSS). Purely
    // a scroll aid alongside swipe; doesn't change which photo is selected.
    (function initThumbNavArrows() {
      const thumbs = root.querySelector('.lc__thumbs');
      const prevBtn = root.querySelector('[data-lc-thumbs-prev]');
      const nextBtn = root.querySelector('[data-lc-thumbs-next]');
      if (!thumbs || !prevBtn || !nextBtn) return;

      function scrollThumbs(direction) {
        const firstThumb = thumbs.querySelector('.lc__thumb');
        const gap = 10;
        const step = firstThumb ? firstThumb.getBoundingClientRect().width + gap : 74;
        thumbs.scrollBy({ left: direction * step, behavior: 'smooth' });
      }

      prevBtn.addEventListener('click', () => scrollThumbs(-1));
      nextBtn.addEventListener('click', () => scrollThumbs(1));
    })();

    async function saveSelectionOnBackend() {
      const selections = { size: state.sizeConfig.sizeCode };
      otherGroups.forEach((g) => {
        if (!isGroupVisible(g)) return; // e.g. a stale paint_style pick after leaving Premium
        selections[g.handle] = state.selections[g.handle];
      });

      const response = await fetch(data.apiBase + '/configurator-save-selection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId: String(data.productId),
          shopDomain: data.shopDomain,
          selections: JSON.stringify(selections),
          modelId: data.modelId != null ? String(data.modelId) : null,
        }),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || response.statusText);
      }
      const result = await response.json();
      if (!result.selectionId) throw new Error(result.error || 'Failed to save configuration');
      return result;
    }

    async function addConfiguredLineToCart(saved) {
      // Visible properties (Shopify's cart template hides any key starting with "_"), built
      // generically from group display names so this works for any brand/model, not just
      // Franklin. Hidden properties carry what configurator-cart-line.js needs to find this
      // line again and re-price/checkout it correctly.
      const properties = { Size: state.sizeConfig.name };
      otherGroups.forEach((g) => {
        if (!isGroupVisible(g)) return;
        const code = state.selections[g.handle];
        const value = code ? findValueByCode(g.id, code) : null;
        if (value) properties[g.displayName] = value.label;
      });
      properties._configurator = 'true';
      properties._configurationId = saved.selectionId;
      properties._sessionToken = saved.sessionToken;
      // Carries the exact configured photo into the cart (see updateConfiguredHero). Omitted
      // when this size/color/hardware/lock combo hasn't been photographed — cart-products.liquid
      // falls back to the normal placeholder-variant image in that case.
      if (state.configuredImageUrl) properties._configuredImageUrl = state.configuredImageUrl;
      if (state.configuredHardwareImageUrl) properties._configuredHardwareImageUrl = state.configuredHardwareImageUrl;

      // Reconfiguring THIS product (e.g. re-submitting after the cart's "Edit configuration"
      // link, or double-clicking Add to Cart) must replace this product's own previously-added
      // configured line, not sit alongside it as a second cart item for the same safe. Matched
      // on this product's placeholder variant specifically — a configured line for a *different*
      // product/model must be left alone so multiple configured safes can coexist in the cart.
      const existingCart = await fetch('/cart.js').then((r) => r.json());
      const existingConfiguredLine = (existingCart.items || []).find(
        (item) =>
          item.properties &&
          item.properties._configurator === 'true' &&
          String(item.variant_id) === String(data.variantId)
      );
      if (existingConfiguredLine) {
        // Tell the backend this old selection is dead before dropping its cart line — mirrors
        // wireEditConfiguration() in configurator-cart-line.js. Without this, a selection that
        // already reached "checkoutCreated" (shopper started checkout, then came back and
        // reconfigured instead of finishing) is left orphaned: still "checkoutCreated" with an
        // open draft order, even though its cart line is gone. If it (or a stray duplicate line
        // for it) ever ends up back in a checkout request, createCheckoutDraftOrder.ts rejects
        // the whole checkout with "Selection is checkoutCreated and can no longer be checked
        // out" — this happened in production, see conversation. Best-effort: a failure here
        // shouldn't block adding the new configuration.
        const oldProps = existingConfiguredLine.properties || {};
        if (oldProps._configurationId && oldProps._sessionToken) {
          try {
            await fetch(data.apiBase + '/configurator-abandon', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                selectionId: oldProps._configurationId,
                sessionToken: oldProps._sessionToken,
              }),
            });
          } catch (error) {
            console.warn('AMSEC configurator: failed to abandon replaced selection', error);
          }
        }

        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: existingConfiguredLine.key, quantity: 0 }),
        });
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: data.variantId, quantity: 1, properties }],
        }),
      });
      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(text || response.statusText);
      }
    }

    if (ctaEl) {
      ctaEl.addEventListener('click', async () => {
        if (ctaEl.disabled) return;
        const originalText = ctaEl.textContent;
        ctaEl.disabled = true;
        ctaEl.textContent = 'Adding...';
        try {
          const saved = await saveSelectionOnBackend();
          await addConfiguredLineToCart(saved);
          window.location.href = '/cart';
        } catch (err) {
          console.error('AMSEC configurator add-to-cart error:', err);
          alert('Sorry, there was a problem adding your configuration to the cart. Please try again.');
          ctaEl.disabled = false;
          ctaEl.textContent = originalText;
        }
      });
    }

    buildAll();
    initStickyCart();
  }

  document.querySelectorAll('.lc').forEach(init);
})();
